/**
 * Pure utility functions for breadcrumb logic.
 * Can be unit tested without React dependencies.
 */

/**
 * Normalizes a URL segment or label for display.
 * Converts kebab-case/snake_case to Title Case and decodes URI components.
 */
export var normalizeLabel = function normalizeLabel(value) {
  return decodeURIComponent(value).replace(/[-_]+/g, ' ').replace(/\b\w/g, function (_char) {
    return _char.toUpperCase();
  });
};
/**
 * Strips malformed query params (e.g. ?redirect_url=...) from a value.
 */

var cleanQueryValue = function cleanQueryValue(value) {
  return value.split('?')[0];
};
/**
 * Parses a URL and returns breadcrumb items (labels only) based on the URL structure.
 *
 * Use Case 1: url with one level like /{root}?menu={menuItem}&level=1
 * - Returns: [rootPageName, menuItem]
 * - Example: /bricks?menu=examinations&level=1, rootPageName="Your work" → ["Your work", "Examinations"]
 *
 * Use Case 2: url with path like /{root}/{pageName}/{pageId}?level=2&name={pageDetailName}
 * - Returns: [pageName, pageDetailName]
 * - Example: /bricks/examinations/123?name=IELTS%20123&level=2 → ["Examinations", "IELTS 123"]
 *
 * Use Case 3: url with ?menu= (feature) and ?name=
 * - Returns: [menuItem, pageDetailName, featureName]
 * - Example: ?menu=examinations&menu=grading&level=2&name=IELTS%20123 → ["Examinations", "IELTS 123", "Grading"]
 */


export var getBreadCrumbItems = function getBreadCrumbItems(params) {
  var items = getBreadCrumbItemsWithLinks(params);
  return items.map(function (item) {
    return item.label;
  });
};
/**
 * Parses a URL and returns breadcrumb items with optional href for navigation.
 * Every item (except last) has href per README - all breadcrumb items should be links.
 */

export var getBreadCrumbItemsWithLinks = function getBreadCrumbItemsWithLinks(params) {
  var root = params.root,
      rootPageName = params.rootPageName,
      url = params.url,
      baseUrl = params.baseUrl,
      menuOverrides = params.menuOverrides,
      _params$dashboardPath = params.dashboardPath,
      dashboardPath = _params$dashboardPath === void 0 ? 'dashboard' : _params$dashboardPath,
      level = params.level;

  if (!url) {
    return [{
      label: rootPageName
    }];
  }

  try {
    var parsedUrl = new URL(url, baseUrl);
    var pathname = parsedUrl.pathname;
    var pathSegments = pathname.split('/').filter(Boolean);
    var rootIndex = pathSegments.indexOf(root);
    var segments = rootIndex >= 0 ? pathSegments.slice(rootIndex + 1) : pathSegments;
    var menuValues = parsedUrl.searchParams.getAll('menu');
    var menuFromQuery = parsedUrl.searchParams.get('menu');
    var nameFromQuery = parsedUrl.searchParams.get('name');
    var subFromQuery = parsedUrl.searchParams.get('sub');
    var levelFromQuery = parsedUrl.searchParams.get('level');

    var buildMenuHref = function buildMenuHref(menuValue) {
      var base = "".concat(baseUrl.replace(/\/$/, '')).concat(root ? "/".concat(root) : ''); // Per README: menu href uses level=1 when on detail page (level=2). Derive from URL if not passed.

      var menuLevel = level !== null && level !== void 0 ? level : levelFromQuery ? Math.max(1, parseInt(levelFromQuery, 10) - 1) : undefined;

      if (menuLevel !== undefined) {
        return "".concat(base, "?menu=").concat(menuValue, "&level=").concat(menuLevel);
      }

      return "".concat(base, "/").concat(dashboardPath, "?menu=").concat(menuValue);
    }; // Use Case 2 & 3: Detail page - path has /{pageName}/{pageId} or /{pageName}/detail/{pageId}


    var hasDetailPath = segments.length >= 2 && nameFromQuery;

    if (nameFromQuery && hasDetailPath) {
      var _ref, _menuOverrides$pathSe;

      var pageDetailName = decodeURIComponent(cleanQueryValue(nameFromQuery));
      var pathSegment = segments[0]; // Use Case 3: menuItem > pageDetailName > featureName
      // - Multiple ?menu= params: first=menuItem, last=featureName
      // - Single ?menu= with level=2: path=menuItem, menu=featureName (per README)
      // - ?sub= param: path/menu=menuItem, sub=featureName

      var isSubPage = levelFromQuery === '2' || level === 2;
      var featureName = menuValues.length >= 2 ? normalizeLabel(cleanQueryValue(menuValues[menuValues.length - 1])) : menuValues.length === 1 && isSubPage ? normalizeLabel(cleanQueryValue(menuValues[0])) : subFromQuery ? normalizeLabel(cleanQueryValue(subFromQuery)) : null;
      var menuItem = menuValues.length >= 2 ? normalizeLabel(menuValues[0]) : menuValues.length === 1 && isSubPage ? normalizeLabel(pathSegment) : menuValues.length >= 1 ? normalizeLabel(menuValues[0]) : segments.length > 0 ? normalizeLabel(pathSegment) : rootPageName; // When single menu is feature (isSubPage), menu href uses path segment; otherwise use menu param

      var menuValue = (_ref = (_menuOverrides$pathSe = menuOverrides === null || menuOverrides === void 0 ? void 0 : menuOverrides[pathSegment]) !== null && _menuOverrides$pathSe !== void 0 ? _menuOverrides$pathSe : menuValues.length === 1 && isSubPage ? pathSegment : menuValues[0]) !== null && _ref !== void 0 ? _ref : pathSegment;

      if (featureName) {
        // Use Case 3: menuItem > pageDetailName > featureName
        // Per README: detail href = /bricks/examinations/123?name=IELTS%20123&level=2 (no menu params)
        var detailParams = new URLSearchParams();
        if (nameFromQuery) detailParams.set('name', nameFromQuery);
        if (levelFromQuery) detailParams.set('level', levelFromQuery);else if (level !== undefined) detailParams.set('level', level.toString());
        var detailSearch = detailParams.toString();
        var detailPath = detailSearch ? "".concat(parsedUrl.pathname, "?").concat(detailSearch) : parsedUrl.pathname;
        var detailHref = "".concat(parsedUrl.origin).concat(detailPath);
        return [{
          label: menuItem,
          href: buildMenuHref(menuValue)
        }, {
          label: pageDetailName,
          href: detailHref
        }, {
          label: featureName
        }];
      } // Use Case 2: menuItem > pageDetailName (last item is current page, no href)


      return [{
        label: menuItem,
        href: buildMenuHref(menuValue)
      }, {
        label: pageDetailName
      }];
    } // Use Case 1: Root page with one level - return rootPageName > menuItem (per README)


    if (menuFromQuery && !hasDetailPath) {
      var rootHref = "".concat(baseUrl.replace(/\/$/, '')).concat(root ? "/".concat(root) : '');

      var _menuItem = normalizeLabel(menuFromQuery);

      return [{
        label: rootPageName || root,
        href: rootHref
      }, {
        label: _menuItem
      }];
    }

    return [{
      label: rootPageName
    }];
  } catch (_unused) {
    return [{
      label: rootPageName
    }];
  }
};